import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Mail, Lock, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: email, 2: code & new password
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim()
      });

      toast.success('Reset code sent to your email!');
      // In development, show the code
      if (response.data.reset_code) {
        toast.info(`Reset code: ${response.data.reset_code}`);
      }
      setStep(2);
    } catch (error) {
      console.error('Error sending reset code:', error);
      toast.error('Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!resetCode.trim() || !newPassword.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: email.trim(),
        reset_code: resetCode.trim(),
        new_password: newPassword
      });

      toast.success('Password reset successful!');
      navigate('/signin');
    } catch (error) {
      console.error('Error resetting password:', error);
      const message = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundColor: '#2e0510'
      }}
    >
      <Card
        className="w-full max-w-md shadow-2xl border-2 relative z-10"
        style={{
          backgroundColor: '#260e10',
          borderColor: 'rgba(255, 200, 87, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
        }}
      >
        <CardHeader className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="w-7 h-7 animate-pulse" style={{ color: '#FFC857' }} />
            <CardTitle className="text-3xl font-bold" style={{ color: '#FFC857', textShadow: '0 0 20px rgba(255, 200, 87, 0.5)' }}>
              {step === 1 ? 'Forgot Password' : 'Reset Password'}
            </CardTitle>
          </div>
          <CardDescription className="text-base" style={{ color: '#F5E3D7' }}>
            {step === 1 ? 'Enter your email to receive a reset code' : 'Enter the code and your new password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#FFC857', opacity: 0.7 }} />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 text-base border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                    style={{
                      backgroundColor: '#311214',
                      color: '#F5E3D7'
                    }}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-lg font-bold transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: '#f3b15b',
                  color: '#47030F',
                  boxShadow: '0 2px 10px rgba(255, 200, 87, 0.3)',
                  borderRadius: '8px'
                }}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <div className="flex items-center justify-center">
                <Link
                  to="/signin"
                  className="flex items-center gap-2 text-sm hover:underline"
                  style={{ color: '#FFC857' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Reset Code</label>
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  maxLength={6}
                  className="h-12 text-base text-center tracking-widest"
                  style={{
                    backgroundColor: '#311214',
                    color: '#F5E3D7'
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#FFC857', opacity: 0.7 }} />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-11 h-12 text-base border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                    style={{
                      backgroundColor: '#311214',
                      color: '#F5E3D7'
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Confirm New Password</label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 text-base border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                  style={{
                    backgroundColor: '#311214',
                    color: '#F5E3D7'
                  }}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-lg font-bold transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: '#f3b15b',
                  color: '#47030F',
                  boxShadow: '0 2px 10px rgba(255, 200, 87, 0.3)',
                  borderRadius: '8px'
                }}
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 text-sm hover:underline"
                  style={{ color: '#FFC857' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;